import Vue from 'vue'
import TemplatePreview from '@/components/Templates/preview.vue'
import { validateTemplateSnapshot, templateHome } from '@/editor/core/component-template'
import { uploadImageFile } from '@/api/image-resources'
import { uploadedImageFile } from '@/utils/image-resources'
import { builtinResourceUrl } from './builtin-resource-url'

const pause = ms => new Promise(resolve => setTimeout(resolve, ms))
export function coverDimensions (input) {
  const value = validateTemplateSnapshot(input), home = templateHome(value)
  const width = value.kind === 'project' ? home.config.width : value.width
  const height = value.kind === 'project' ? home.config.height : value.height
  const ratio = Math.min(640 / width, 640 / height, 1)
  return { width: Math.max(64, Math.round(width * ratio)), height: Math.max(64, Math.round(height * ratio)) }
}
export function coverFileUrl (fileId) {
  return typeof fileId === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(fileId) ? builtinResourceUrl(fileId) || '/jetlinks/file/' + fileId + '.png' : ''
}
// 只挂载一个隔离的静态配置视图；不截图编辑器的缩放、选框、尺条或工具栏。
export async function captureResourceCover (input, isCurrent = () => true, { projectCover = false } = {}) {
  const snapshot = projectCover ? input : validateTemplateSnapshot(input)
  const ratio = Math.min(640 / snapshot.width, 640 / snapshot.height, 1)
  const size = projectCover ? { width: Math.max(64, Math.round(snapshot.width * ratio)), height: Math.max(64, Math.round(snapshot.height * ratio)) } : coverDimensions(snapshot)
  const home = templateHome(snapshot), width = snapshot.kind === 'project' ? home.config.width : snapshot.width, height = snapshot.kind === 'project' ? home.config.height : snapshot.height
  if (typeof window === 'undefined' || typeof window.html2canvas !== 'function') throw new Error('封面生成工具未加载，请刷新页面后重试')
  let vm, host
  const check = () => { if (!isCurrent()) throw new Error('已离开当前编辑，封面未保存') }
  try {
    check(); host = document.createElement('div')
    // 保持坐标在视口内，兼容画布截图及旧版截图库，
    // 用底层层级隔离，不使用 opacity/visibility（它们也会进入截图）。
    Object.assign(host.style, { position: 'fixed', left: '0', top: '0', zIndex: '-1', width: width + 'px', height: height + 'px', pointerEvents: 'none' })
    host.setAttribute('aria-hidden', 'true'); document.body.appendChild(host)
    vm = new Vue({ render: h => h(TemplatePreview, { props: { value: snapshot, height, capture: true, projectCover } }) }).$mount()
    host.appendChild(vm.$el); await Vue.nextTick()
    // mounted 发生在插入之前，先按实际宿主宽度重新计算缩放。
    if (vm.$children[0] && vm.$children[0].resize) vm.$children[0].resize()
    await Vue.nextTick()
    if (document.fonts && document.fonts.ready) await Promise.race([document.fonts.ready, pause(800)])
    const prepareCharts = () => { if (vm.$children[0] && vm.$children[0].prepareCapture) vm.$children[0].prepareCapture() }
    prepareCharts()
    // 旧版图表及数字组件没有统一透传 animation=false，等其首轮动画完成。
    const animated = snapshot.kind === 'project' ? home.nodes : snapshot.nodes
    const hasAnimation = nodes => nodes.some(node => node.children ? hasAnimation(node.children) : ['bar', 'line', 'pie', 'pictorialbar', 'radar', 'scatter', 'funnel', 'gauge', 'wordcloud', 'flop', 'progress'].includes(node.component.name))
    await pause(hasAnimation(animated) ? 2200 : 350); check()
    const images = Array.from(host.querySelectorAll('img'))
    await Promise.all(images.map(image => new Promise((resolve, reject) => {
      if (image.complete) return image.naturalWidth ? resolve() : reject(new Error('封面中的图片未能加载，请检查图片地址'))
      const finish = ok => { clearTimeout(timer); image.onload = image.onerror = null; ok ? resolve() : reject(new Error('封面中的图片未能加载，请重试')) }
      const timer = setTimeout(() => finish(false), 5000)
      image.onload = () => finish(true); image.onerror = () => finish(false)
    })))
    check()
    // SVG序列化为图片时需要明确固有尺寸，否则细长管线会退回默认150px高度。
    Array.from(host.querySelectorAll('svg')).forEach(svg => {
      const bounds = svg.getBoundingClientRect()
      if (bounds.width && bounds.height) { svg.setAttribute('width', bounds.width); svg.setAttribute('height', bounds.height) }
    })
    // 图片、字体及旧图表的异步初始化结束后，再按最终尺寸同步最后一帧。
    prepareCharts(); await Vue.nextTick()
    // 组件按逻辑尺寸渲染；仅在输出位图时缩小，避免嵌套CSS scale截断文字、SVG和图表。
    const canvas = await window.html2canvas(vm.$el, { scale: Math.min(size.width / width, size.height / height), width, height, windowWidth: Math.max(window.innerWidth || 0, width), windowHeight: Math.max(window.innerHeight || 0, height), useCORS: true, allowTaint: false, backgroundColor: null, logging: false, imageTimeout: 5000 })
    check()
    const pixels = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height).data
    if (!pixels.some((value, index) => index % 4 === 3 && value > 0)) throw new Error('封面内容为空，请刷新后重新保存')
    const blob = await new Promise((resolve, reject) => canvas.toBlob(value => value ? resolve(value) : reject(new Error('封面生成失败')), 'image/png'))
    if (blob.size > 1024 * 1024) throw new Error('封面超过1 MB，请简化图片后重试')
    return blob
  } catch (error) { throw new Error(error.message || '封面生成失败，请重试') }
  finally { if (vm) vm.$destroy(); if (host) host.remove() }
}
export async function saveResourceCover (snapshot, isCurrent = () => true, options = {}) {
  const blob = await captureResourceCover(snapshot, isCurrent, options)
  if (!isCurrent()) throw new Error('已离开当前编辑，封面未保存')
  const response = await uploadImageFile(new File([blob], 'resource-cover.png', { type: 'image/png' }))
  if (!isCurrent()) throw new Error('已离开当前编辑，封面未保存')
  return uploadedImageFile(response).id
}
